// src/features/academic/components/QuizProgressBar.jsx
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Circle } from 'lucide-react';

/**
 * Quiz Progress Bar Component
 * Shows progress through the quiz with indicators for correct/incorrect answers
 */
const QuizProgressBar = ({ 
  currentIndex, 
  totalQuestions, 
  userAnswers = {}, 
  questions = [], 
  compact = false 
}) => {
  // Memoize the answer status calculations for better performance
  const answerStatuses = useMemo(() => {
    return Array.from({ length: totalQuestions }).map((_, index) => {
      if (!userAnswers[index] || !questions[index]) return null;
      return userAnswers[index] === questions[index].correctAnswer;
    });
  }, [questions, userAnswers, totalQuestions]);
  
  // For large number of questions, use a more compact view
  const useCondensedView = totalQuestions > 10 && !compact;
  
  if (compact) {
    // Compact version - just shows a progress bar
    return (
      <div className="relative h-1.5 w-full bg-gray-200 rounded-full overflow-hidden mb-3" 
           role="progressbar" 
           aria-valuenow={currentIndex + 1} 
           aria-valuemin="1" 
           aria-valuemax={totalQuestions}>
        <motion.div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-teal-500"
          initial={{ width: `${(currentIndex / totalQuestions) * 100}%` }}
          animate={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      </div>
    );
  }
  
  // Condensed view for many questions
  if (useCondensedView) {
    const completedCount = Object.keys(userAnswers).length;
    const correctCount = Object.values(answerStatuses).filter(status => status === true).length;
    
    return (
      <div className="w-full mb-4">
        {/* Bar Indicator */}
        <div className="relative h-2 w-full bg-gray-200 rounded-full overflow-hidden mb-2">
          <motion.div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-teal-500"
            initial={{ width: `${(currentIndex / totalQuestions) * 100}%` }}
            animate={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>
        
        {/* Stats Display */}
        <div className="flex justify-between text-xs">
          <div>
            <span className="font-medium text-gray-700">Question {currentIndex + 1} of {totalQuestions}</span>
          </div>
          <div className="flex space-x-3">
            <span>
              <CheckCircle size={14} className="inline text-green-500 mr-1" />
              <span className="text-gray-600">{correctCount}</span>
            </span>
            <span>
              <XCircle size={14} className="inline text-red-500 mr-1" />
              <span className="text-gray-600">{completedCount - correctCount}</span>
            </span>
          </div>
        </div>
      </div>
    );
  }
  
  // Standard view - shows indicators for each question
  return (
    <div className="w-full flex items-center justify-center mb-4">
      <div className="flex items-center space-x-1">
        {Array.from({ length: totalQuestions }).map((_, index) => {
          const status = answerStatuses[index];
          const isCurrent = currentIndex === index;
          
          return (
            <div key={index} className="flex items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ 
                  opacity: 1, 
                  scale: isCurrent ? 1.2 : 1,
                }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                {status === true && (
                  <CheckCircle 
                    size={18} 
                    className="text-green-500 fill-green-100" 
                  />
                )}
                {status === false && (
                  <XCircle 
                    size={18} 
                    className="text-red-500 fill-red-100" 
                  />
                )}
                {status === null && (
                  <Circle 
                    size={18} 
                    className={`${
                      isCurrent 
                        ? 'text-teal-500 fill-teal-100' 
                        : index < currentIndex 
                          ? 'text-gray-400 fill-gray-100' 
                          : 'text-gray-300 fill-gray-50'
                    }`} 
                  />
                )}
              </motion.div>
              
              {/* Connecting line */}
              {index < totalQuestions - 1 && (
                <div 
                  className={`h-0.5 w-1 ${
                    index < currentIndex 
                      ? 'bg-green-200' 
                      : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuizProgressBar;